import { useMemo, useState } from "react";
import { eventSchedule, type Inquiry } from "../types";
import {
  generateReservationQuote, sendReservationProposal, syncReservation, ReservationApiError,
  type GroupEventRate, type ReservationCatalog, type ReservationQuoteResult, type ReservationStatus,
} from "../eventsApi";

// "Generate Quote in New System" (tribal-axe#10). The Reservation System prices
// the event, keeps the Reservation, issues the Group Deposit Link and emails the
// Proposal; HQ only sends the event details. HQ/reservation-group-events.md.
type Props = {
  inquiry: Inquiry;
  catalog: ReservationCatalog;
  onClose: () => void;
  onChanged: (patch: Partial<Inquiry>) => void;
};

const RATE_LABELS: Record<GroupEventRate, string> = { no_food: "No Food", with_food: "With Food" };
const dollars = (cents: number) => `$${(cents / 100).toFixed(2)}`;
const input = "w-full rounded-md border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-white focus:border-tribal-red focus:outline-none";
const label = "block text-xs text-tribal-muted mb-1";
const primary = "rounded-md bg-tribal-red px-4 py-2 text-sm font-medium text-white hover:bg-tribal-red-dark disabled:opacity-50";
const secondary = "rounded-md border border-tribal-border px-3 py-1.5 text-xs text-white hover:border-tribal-red disabled:opacity-50";

type Failure = { detail: string; retry: boolean };
const failure = (err: unknown): Failure => err instanceof ReservationApiError
  ? { detail: err.message, retry: err.retry }
  : { detail: (err as Error).message || "Something went wrong.", retry: false };

// "18:00" or "1800" from the inquiry form; vague preferences leave it unset.
function preferredStart(time?: string): string {
  const match = /^(\d{1,2}):?(\d{2})$/.exec(time ?? "");
  return match ? `${match[1].padStart(2, "0")}:${match[2]}` : "";
}

export function ReservationQuoteModal({ inquiry, catalog, onClose, onChanged }: Props) {
  const [experienceId, setExperienceId] = useState(catalog.experiences[0]?.id ?? "");
  const experience = catalog.experiences.find(e => e.id === experienceId);
  const rates = (Object.keys(RATE_LABELS) as GroupEventRate[]).filter(r => experience?.groupRates[r]?.length);
  const [rate, setRate] = useState<GroupEventRate>(inquiry.food_interest ? "with_food" : "no_food");
  const groupEventRate = rates.includes(rate) ? rate : rates[0];
  const ladder = (groupEventRate && experience?.groupRates[groupEventRate]) || [];
  // A re-quote starts from the Reservation as last synced, so it keeps edits made there.
  const schedule = eventSchedule(inquiry);
  const [minutes, setMinutes] = useState(schedule.hours * 60);
  const durationMinutes = ladder.some(d => d.minutes === minutes) ? minutes : ladder[0]?.minutes;
  const [localDate, setLocalDate] = useState(schedule.date ?? "");
  const [start, setStart] = useState(preferredStart(schedule.time));
  const [partySize, setPartySize] = useState(schedule.groupSize);
  const [quantities, setQuantities] = useState<Record<string, number>>({});

  const [quote, setQuote] = useState<ReservationQuoteResult | null>(null);
  const [message, setMessage] = useState("");
  // The Reservation System's saved Proposal messages, edited in its Settings > Templates (ReservationSystem #281).
  const templates = catalog.proposalTemplates ?? [];
  const [templateId, setTemplateId] = useState("");
  const [proposal, setProposal] = useState<{ id: string; state: string } | null>(null);
  const [status, setStatus] = useState<ReservationStatus | null>((inquiry.reservation_status as ReservationStatus | null) ?? null);
  const [busy, setBusy] = useState<"" | "quote" | "proposal" | "sync">("");
  const [errors, setErrors] = useState<{ quote?: Failure; proposal?: Failure; sync?: Failure }>({});
  const [copied, setCopied] = useState(false);

  const applied = quote?.quote ?? inquiry.reservation_quote ?? null;
  const limits = experience?.groupGuestLimits;
  const startMinute = start ? Number(start.slice(0, 2)) * 60 + Number(start.slice(3, 5)) : NaN;
  const invalid = !experience ? "No Experience offers Group Events."
    : !groupEventRate || !durationMinutes ? "This Experience has no Group Event prices."
    : !/^\d{4}-\d{2}-\d{2}$/.test(localDate) ? "Choose the event date."
    : !Number.isInteger(startMinute) || startMinute % 30 ? "Choose a start on the half hour."
    : !Number.isInteger(partySize) || partySize < 1 ? "Set the guest count."
    : (limits?.minimum && partySize < limits.minimum) || (limits?.maximum && partySize > limits.maximum)
      ? `Group Events take ${limits?.minimum ?? 1}–${limits?.maximum ?? "any"} guests.` : "";
  const estimate = useMemo(() => {
    const step = ladder.find(d => d.minutes === durationMinutes);
    return step ? step.pricePerPersonCents * partySize : null;
  }, [ladder, durationMinutes, partySize]);

  async function run<T>(kind: "quote" | "proposal" | "sync", call: () => Promise<T>, done: (value: T) => void) {
    setBusy(kind); setErrors(e => ({ ...e, [kind]: undefined }));
    try { done(await call()); }
    catch (err) { setErrors(e => ({ ...e, [kind]: failure(err) })); }
    finally { setBusy(""); }
  }

  const generate = (retry = false) => run("quote", () => generateReservationQuote(inquiry.id, retry ? { retry: true } : {
    experienceId: experience!.id, groupEventRate: groupEventRate!, localDate, startMinute, durationMinutes: durationMinutes!, partySize,
    items: Object.entries(quantities).filter(([, q]) => q > 0).map(([catalogItemId, quantity]) => ({ catalogItemId, quantity })),
  }), result => {
    setQuote(result); setProposal(null); setCopied(false);
    onChanged({ reservation_code: result.reservationCode, reservation_quote: result.quote,
      ...(["new", "contacted"].includes(inquiry.status) ? { status: "quoted" as const } : {}) });
  });
  const send = (retry = false) => run("proposal", () => sendReservationProposal(inquiry.id, retry ? { retry: true } : { message: message.trim() || undefined }),
    result => setProposal(result));
  const refresh = () => run("sync", () => syncReservation(inquiry.id), result => {
    setStatus(result.status);
    onChanged({ reservation_status: result.status as unknown as Record<string, unknown>, status: result.inquiryStatus as Inquiry["status"], reservation_quote: result.reservationQuote });
  });
  const copy = async (url: string) => { try { await navigator.clipboard.writeText(url); setCopied(true); } catch { /* the URL is shown for manual copy */ } };

  const latestProposal = status?.proposals?.[0];
  const Problem = ({ value, onRetry }: { value?: Failure; onRetry: () => void }) => value ? (
    <div role="alert" className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300 flex items-center justify-between gap-2">
      <span>{value.detail}</span>
      {value.retry && <button type="button" className={secondary} disabled={!!busy} onClick={onRetry}>Retry</button>}
    </div>
  ) : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-2xl mx-4 rounded-xl border border-tribal-border bg-tribal-surface shadow-2xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between border-b border-tribal-border px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-white">Generate Quote in New System</h2>
            <p className="text-xs text-tribal-muted mt-0.5">
              {inquiry.first_name} {inquiry.last_name} — {inquiry.email}
              {inquiry.reservation_code && <> · Reservation {inquiry.reservation_code}</>}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-tribal-muted hover:text-white text-lg leading-none" aria-label="Close">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <section className="space-y-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-tribal-muted">Event</h3>
            <div className="grid grid-cols-2 gap-3">
              {catalog.experiences.length > 1 && <div className="col-span-2">
                <label className={label} htmlFor="rq-experience">Experience</label>
                <select id="rq-experience" className={input} value={experienceId} onChange={e => setExperienceId(e.target.value)}>
                  {catalog.experiences.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
                </select>
              </div>}
              <div>
                <label className={label} htmlFor="rq-rate">Group Event Rate</label>
                <select id="rq-rate" className={input} value={groupEventRate ?? ""} onChange={e => setRate(e.target.value as GroupEventRate)}>
                  {rates.map(r => <option key={r} value={r}>{RATE_LABELS[r]}</option>)}
                </select>
              </div>
              <div>
                <label className={label} htmlFor="rq-duration">Duration</label>
                <select id="rq-duration" className={input} value={durationMinutes ?? ""} onChange={e => setMinutes(Number(e.target.value))}>
                  {ladder.map(d => <option key={d.minutes} value={d.minutes}>{d.minutes / 60} hours · {dollars(d.pricePerPersonCents)}/guest</option>)}
                </select>
              </div>
              <div>
                <label className={label} htmlFor="rq-date">Event date</label>
                <input id="rq-date" type="date" className={input} value={localDate} onChange={e => setLocalDate(e.target.value)} />
              </div>
              <div>
                <label className={label} htmlFor="rq-start">Start (half hour)</label>
                <input id="rq-start" type="time" step={1800} className={input} value={start} onChange={e => setStart(e.target.value)} />
              </div>
              <div>
                <label className={label} htmlFor="rq-guests">Guests</label>
                <input id="rq-guests" type="number" min={1} className={input} value={partySize} onChange={e => setPartySize(Math.floor(Number(e.target.value)))} />
                {limits && <p className="text-xs text-tribal-muted mt-1">Group Events: {limits.minimum ?? 1}–{limits.maximum ?? "any"} guests</p>}
              </div>
            </div>
          </section>

          {catalog.items.length > 0 && <section className="space-y-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-tribal-muted">Food and packages</h3>
            {catalog.items.map(item => (
              <div key={item.id} className="flex items-center gap-3 rounded-md border border-tribal-border bg-tribal-bg px-3 py-2">
                <span className="flex-1 text-sm text-white">{item.name}
                  <span className="text-xs text-tribal-muted"> · {dollars(item.unitPriceCents)} {item.pricingBasis === "per_person" ? "per guest" : "each"}</span></span>
                {item.pricingBasis === "per_person" ? (
                  <input type="checkbox" aria-label={`Include ${item.name}`} checked={(quantities[item.id] ?? 0) > 0}
                    onChange={e => setQuantities(q => ({ ...q, [item.id]: e.target.checked ? 1 : 0 }))} />
                ) : (
                  <input type="number" min={0} aria-label={`${item.name} quantity`} value={quantities[item.id] ?? 0}
                    onChange={e => setQuantities(q => ({ ...q, [item.id]: Math.max(0, Math.floor(Number(e.target.value))) }))}
                    className="w-16 rounded border border-tribal-border bg-tribal-surface px-2 py-1 text-center text-xs text-white" />
                )}
              </div>
            ))}
          </section>}

          <section className="space-y-2">
            {estimate !== null && !quote && <p className="text-xs text-tribal-muted">
              Activity {dollars(estimate)} before food, taxes and fees. The Reservation System prices the Quote.</p>}
            {invalid && <p className="text-xs text-amber-400">{invalid}</p>}
            <Problem value={errors.quote} onRetry={() => generate(true)} />
            <button type="button" className={primary} disabled={!!invalid || !!busy} onClick={() => generate()}>
              {busy === "quote" ? "Generating…" : applied ? "Generate New Quote Version" : "Generate Quote"}
            </button>
            {applied && !quote && <p className="text-xs text-tribal-muted">A new version replaces Quote version {applied.version} and its deposit link.</p>}
          </section>

          {applied && <section className="rounded-md border border-tribal-border p-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-tribal-muted">Quote</span>
              <span className="text-white">Version {applied.version}{(quote?.reservationCode ?? inquiry.reservation_code) && ` · ${quote?.reservationCode ?? inquiry.reservation_code}`}</span></div>
            <div className="flex justify-between"><span className="text-tribal-muted">Total</span><span className="text-white">{dollars(applied.totalCents)}</span></div>
            <div className="flex justify-between"><span className="text-tribal-muted">Deposit</span><span className="text-white">{dollars(applied.depositCents)}</span></div>
            {quote?.depositLink && <div className="space-y-1">
              <div className="flex justify-between"><span className="text-tribal-muted">Deposit link</span><span className="text-white">{quote.depositLink.state}</span></div>
              <div className="flex items-center gap-2">
                <input readOnly className={`${input} text-xs`} value={quote.depositLink.url} aria-label="Group Deposit Link" onFocus={e => e.target.select()} />
                <button type="button" className={secondary} onClick={() => copy(quote.depositLink!.url)}>{copied ? "Copied" : "Copy"}</button>
              </div>
              <p className="text-xs text-tribal-muted">The Proposal email carries this link. Anyone with it can pay the deposit, so share it only with the guest.</p>
            </div>}

            <div className="pt-2 space-y-2">
              {!!templates.length && <div>
                <label className={label} htmlFor="rq-template">Saved Proposal message</label>
                <select id="rq-template" className={input} value={templateId} onChange={e => {
                  const chosen = templates.find(t => t.id === e.target.value);
                  setTemplateId(e.target.value); if (chosen) setMessage(chosen.body.slice(0, 2000));
                }}>
                  <option value="">Choose a saved message</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <p className="mt-1 text-xs text-tribal-muted">Choosing a saved message replaces the text below. You can edit it before sending.</p>
              </div>}
              <label className={label} htmlFor="rq-message">Message in the Proposal (optional)</label>
              <textarea id="rq-message" rows={message.length > 200 ? 8 : 3} maxLength={2000} className={`${input} resize-none text-xs`} value={message} onChange={e => setMessage(e.target.value)} />
              <Problem value={errors.proposal} onRetry={() => send(true)} />
              <button type="button" className={primary} disabled={!!busy} onClick={() => send()}>
                {busy === "proposal" ? "Sending…" : `Send Proposal ${proposal ? "again" : `for version ${applied.version}`}`}
              </button>
              {proposal && <p role="status" className="text-xs text-green-400">
                {proposal.state === "queued" ? "Proposal queued. The Reservation System sends it from events@, and replies come to this thread." : `Proposal ${proposal.state}.`}</p>}
            </div>
          </section>}

          {(inquiry.reservation_id || quote) && <section className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-tribal-muted">Reservation status</h3>
              <button type="button" className={secondary} disabled={!!busy} onClick={refresh}>{busy === "sync" ? "Checking…" : "Refresh"}</button>
            </div>
            <Problem value={errors.sync} onRetry={refresh} />
            {status ? <div className="space-y-1 text-xs">
              <div className="flex justify-between"><span className="text-tribal-muted">Reservation</span><span className="text-white">{status.reservationStatus}</span></div>
              <div className="flex justify-between"><span className="text-tribal-muted">Payment</span>
                <span className="text-white">{status.paymentStatus} · {dollars(status.receivedCents ?? 0)} received</span></div>
              {status.depositLink && <div className="flex justify-between"><span className="text-tribal-muted">Deposit link</span>
                <span className="text-white">{status.depositLink.state}{status.depositLink.paidAt && ` · paid ${new Date(status.depositLink.paidAt).toLocaleString()}`}</span></div>}
              {latestProposal && <div className="flex justify-between"><span className="text-tribal-muted">Latest Proposal</span>
                <span className="text-white">version {latestProposal.quoteVersion} · {latestProposal.deliveryStatus ?? latestProposal.state}
                  {latestProposal.error || latestProposal.stopReason ? ` · ${latestProposal.error ?? latestProposal.stopReason}` : ""}</span></div>}
              {!!status.reviewItems?.length && <p className="text-amber-400">{status.reviewItems.length} item(s) waiting for staff review in the Reservation System.</p>}
              {inquiry.reservation_synced_at && <p className="text-tribal-muted">Checked hourly; last read {new Date(inquiry.reservation_synced_at).toLocaleString()}.</p>}
            </div> : <p className="text-xs text-tribal-muted">Not read yet. It updates hourly, or press Refresh.</p>}
          </section>}
        </div>
      </div>
    </div>
  );
}
